/**
 * The words on a drop's list cell: the list(s) this very drop goes to, with
 * an icon for how it got them — pinned here, the group's one list, every
 * list each drop (×N), or this drop's turn (alternate). Full names on hover.
 * The group's own choice (one / every / alternate) says "auto" first (20/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Hash, Layers, List, Pin, Repeat2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import { AutoTag } from './AutoTag';
import { emptyListsWords, type GroupListsKind, type GroupListsView } from './planned-lists';

const ICONS: Record<GroupListsKind, typeof List> = { pinned: Pin, rank: Hash, one: List, each: Layers, alternate: Repeat2 };

const HOW: Record<GroupListsKind, string> = {
  pinned: 'pinned on this drop',
  rank: 'this rank of the group’s lists of the day — it follows the group every day',
  one: "the group's list of the day",
  each: 'every list, one drop each',
  alternate: 'the lists take turns — this is this drop’s',
};

/** The kinds that follow the group's rule — not a list or rank chosen on this drop. */
const AUTOMATIC: ReadonlySet<GroupListsKind> = new Set(['one', 'each', 'alternate']);

export function listCellTitle(view: GroupListsView, words: string): string {
  if (!view.names.length || view.stale) return words;
  return `${view.names.join(', ')} — ${HOW[view.kind]}${view.planned ? '' : ' (this drop is not in today’s plan)'}`;
}

export const ListCellLabel: FC<{ view: GroupListsView; listCount: number | null }> = ({ view, listCount }) => {
  const Icon = ICONS[view.kind];
  const known = view.names.length > 0 && !view.stale;
  const listed = view.kind === 'rank' ? `#${view.rank} · ${view.names.join(' · ')}` : view.names.join(' · ');
  const words = known ? listed : emptyListsWords(view, listCount);
  return (
    <>
      {AUTOMATIC.has(view.kind) ? <AutoTag title={`Automatic — ${HOW[view.kind]}`} /> : null}
      <Icon className={cn('h-3.5 w-3.5 shrink-0', view.kind === 'pinned' ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
      <span className={cn('min-w-0 flex-1 truncate text-xs', known ? 'font-medium' : 'italic text-muted-foreground')}>{words}</span>
      {known && view.names.length > 1 ? (
        <span className="shrink-0 rounded bg-primary/10 px-1 font-mono text-[10px] font-semibold text-primary">×{view.names.length}</span>
      ) : null}
    </>
  );
};
